// PCPrevencionTemplate.ts
// Genera el HTML del Dictamen de Seguridad y Prevención a partir de variables.
// Basado en el HTML provisto por el usuario.

export interface DictamenPrevencionVars {
  // Encabezado
  dictamenFolio: string;            // ej: "PCT/DSP/0415/TOTOLAC/2025"
  area: string;                     // ej: "coordinación de proteccion civil"
  fechaLugar: string;               // ej: "TOTOLAC, TLAXCALA; JULIO 28 DE DOS MIL VEINTICINCO"

  // Destinatario
  destinatarioNombre: string;       // ej: "C. MONSERRAT CANO GARCIA"
  giroComercial: string;            // <b>MUEBLERIA</b>
  denominacion: string;             // <b>"MUEBLERIA CANO´S"</b>
  presente?: string;                // por defecto: "presente"

  // Título
  titulo?: string;                  // por defecto: "dictamen de seguridad y prevención"

  // Texto en NEGRITAS (y subrayado) del cuerpo
  encabezadoVerificacion: string;   // <b class="subr">Basándose en la verificación ocular realizada ...</b>
  solicitante: string;              // <b>C. MONSERRAT CANO GARCIA</b>
  ubicacionDireccion: string;       // <b>CARRETERA FEDERAL SUR #23, SAN JUAN, TOTOLAC; TLAXCALA</b>
  fundamentoLegal: string;          // <b class="subr">CON FUNDAMENTO EN LOS ARTÍCULOS 36, 37 Y 38 ...</b>
  cuentaMedidasTexto: string;       // <b class="subr">SI CUENTA CON LAS MEDIDAS MÍNIMAS DE SEGURIDAD</b>
  verificadorNombre: string;        // <b class="subr">MAURICIO CRUZ BERNAL</b>
  determinacionTexto: string;       // <b class="subr">SI</b>

  // Firmas
  firmaNombre: string;              // "IVÁN LANDER CORONA"
  firmaCargo: string;               // "COORDINADOR PROTECCIÓN CIVIL MUNICIPAL"
  vistoBuenoNombre?: string;        // "MAURICIO CRUZ BERNAL" (opcional “V°. B°”)
  firmaImgSrc?: string;             // ruta/URL de imagen de firma (opcional)

  // Opcionales
  autoPrint?: boolean;              // si true, window.print() al cargar
  fontFamily?: string;              // por defecto 'Agency FB'
}

export function renderPCPrevencion(v: DictamenPrevencionVars): string {
  const titulo = v.titulo ?? 'dictamen de seguridad y prevención';
  const presente = v.presente ?? 'presente';
  const font = v.fontFamily ?? 'Agency FB';

  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Dictamen de Prevención</title>
  <style>
    @media print{
      html, body { position: relative; margin: 0; padding: 0; height: auto !important; min-height: auto !important; }
      section, div { page-break-inside: avoid; }
    }
    body {
      position: relative;
      width: 21cm;
      height: 27.9cm; /* Tamaño carta */
      margin: 0;
      font-family: '${escapeCss(font)}';
      font-size: 12pt;
      page-break-inside: avoid;
      margin-bottom: 0;
    }
    .remitente{
      text-align: right;
      margin-top: 50px;
      margin-right: 50px;
      font-weight: bold;
      font-size: 13pt;
      text-transform: uppercase;
    }
    .destinatario{
      text-align: left;
      margin-top: 50px;
      margin-left: 50px;
      font-weight: bold;
      font-size: 13pt;
      text-transform: uppercase;
    }
    .cuerpo{
      text-align: justify;
      margin-right: 50px;
      margin-left: 50px;
      margin-bottom: 50px;
      min-height: 500px;
    }
    .subr{ border-bottom: solid 1px; }
    .titulo{
      font-size: 16pt;
      text-align: center;
      margin-top: 20px;
      font-weight: bold;
      text-transform: uppercase;
    }
    .atento{
      letter-spacing: 5px;
      margin-top: 50px;
      text-align: center;
      text-transform: uppercase;
    }
    .firmas{
      margin-top: 10px;
      align-items: center;
      text-align: center;
      text-transform: uppercase;
    }
    .firma1{
      text-align: center;
      margin: 12px auto 0 auto;
      border-top: solid 1px;
      width: 250px;
    }
    .Vb{
      text-align: right;
      margin-right: 50px;
      font-size: 9pt;
    }
    .legal{
      margin-left: 50px;
      font-family: Arial, Helvetica, sans-serif;
      font-size: 6pt;
    }
    img.firma { display: block; margin: 0 auto 8px auto; max-height: 80px; }
  </style>
</head>
<body>
  <section class="remitente">
    <div>Dictamen: ${escapeHtml(v.dictamenFolio)}</div>
    <div>Area: ${escapeHtml(v.area)}</div>
    <div>${escapeHtml(v.fechaLugar)}</div>
  </section>

  <section class="destinatario">
    <div>${escapeHtml(v.destinatarioNombre)}</div>
    <div>NEGOCIO CON GIRO COMERCIAL: ${escapeHtml(v.giroComercial)}</div>
    <div>DENOMINADO: ${escapeHtml(v.denominacion)}</div>
    <div>${escapeHtml(presente)}</div>
  </section>

  <section class="cuerpo">
    <div class="titulo">${escapeHtml(titulo)}</div>
    <div>
      <b class="subr">${escapeHtml(v.encabezadoVerificacion)}</b>, por medio del personal autorizado, a solicitud de:
      <b>${escapeHtml(v.solicitante)}</b> propietario del establecimiento con GIRO COMERCIAL:
      <b>${escapeHtml(v.giroComercial)}</b>, DENOMINADO:
      <b>${escapeHtml(v.denominacion)}</b>, ubicado en
      <b>${escapeHtml(v.ubicacionDireccion)}</b> y
      <b class="subr">${escapeHtml(v.fundamentoLegal)}</b>, se verifico que
      <b class="subr">${escapeHtml(v.cuentaMedidasTexto)}</b>, dando cumplimiento a las disposiciones que Protección Civil requiere para el seguro y buen funcionamiento del mismo establecimiento.
      <br><br>
      Por lo anterior la coordinación de Protección Civil Municipal a través del personal de verificación
      <b class="subr">${escapeHtml(v.verificadorNombre)}</b> determina que
      <b class="subr">${escapeHtml(v.determinacionTexto)}</b> cuenta con las medidas mínimas de seguridad y prevención requeridas, toda vez que se acreditó lo establecido, se extiende el presente dictamen.
    </div>
  </section>

  <div class="atento">atentamente</div>

  <section class="firmas">
    ${v.firmaImgSrc ? `<img class="firma" src="${escapeAttr(v.firmaImgSrc)}" alt="firma">` : ''}
    <div class="firma1">
      <div>${escapeHtml(v.firmaNombre)}</div>
      <div>${escapeHtml(v.firmaCargo)}</div>
    </div>
  </section>

  ${v.vistoBuenoNombre ? `<div class="Vb">V°. B°<br> ${escapeHtml(v.vistoBuenoNombre)}</div>` : ''}

  <div class="legal">El presente documento tiene valor durante el ejercicio fiscal vigente</div>

  ${v.autoPrint ? `<script>window.onload = function(){ try{ window.print(); }catch(e){} };</script>` : ''}
</body>
</html>`;
}

// Helpers para evitar inyecciones al interpolar variables.
function escapeHtml(input: string): string {
  return String(input)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

// Para atributos (src/href)
function escapeAttr(input: string): string {
  return escapeHtml(input).replace(/"/g, '&quot;');
}

// Para CSS inline (familia tipográfica)
function escapeCss(input: string): string {
  return String(input).replace(/['"\\;]/g, '');
}
